import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../router/all_routes";
import Table from "../../core/common/dataTable/index";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import PageHeader from "../../core/common/commonComponents/PageHeader";
import { exportToCSV, exportToExcel } from "../../core/common/GlobalFunctions/exportTableData";
import StatusFilterHeader from "../../core/common/commonComponents/StatusFilterHeader";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import DeleteModal1 from "../../core/modals/deleteModal1";
import { message } from "antd";
import { filterData } from "../../core/common/GlobalFunctions/tableFilters";
import PfWithdrawalRequestModal from "./modals/PfWithdrawalRequestModal";
import { deletePfWithdrawal, fetchAllPfWithdrawals } from "../../core/data/redux/payroll/Accounting/providentFund/pfWithdrawal/pfWithdrawalActions";
import { useAppConfig } from "../../customhooks/useAppConfig";
import { formatCurrency, formatDate } from "../../core/utils/configFormatters";

const PfWithdrawalRequest = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedStatus, setSelectedStatus] = useState('All');
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const { records = [] } = useSelector((state: any) => state.providentFund.pfWithdrawl);

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
    };

    useEffect(() => {
        if (!records.length) {
            dispatch(fetchAllPfWithdrawals())
        }
    }, [dispatch, records.length])

    const filtereddata = useMemo(() => {
        const filters = selectedStatus && selectedStatus !== "All"
            ? { status: selectedStatus }
            : {};
        return filterData(records, filters, "createdAt", "createdAt", selectedSort);
    }, [records, selectedStatus, selectedSort]);


    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedStatus, selectedSort, records]);

    const handleDeletePfwithdrawal = async (id: number) => {
        try {
            const result = await dispatch(deletePfWithdrawal(id));
            if (result.meta.requestStatus === 'fulfilled') {
                message.success('Withdrawal Request deleted successfully');
                setTableKey((prevKey: any) => prevKey + 1);
                setTableKey((prevKey) => prevKey + 1);
                setIsDeleteModalVisible(false);
            } else {
                message.error((result.payload as { error: string })?.error);
            }
        } catch (error) {
            message.error('Something went wrong');
        }
    };

     const config = useAppConfig();

    const columns = [
        {
            title: "Employee",
            dataIndex: "employeeName",
            render: (_: any, record: any) => (
                <div style={{ width: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <img
                        src={record.employeePictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                        alt={record.employeeName}
                        style={{ width: 32, height: 32, borderRadius: "50%", marginRight: 8 }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/default-avatar.png"; // fallback image
                        }}
                    />
                    <span>{record.employeeName}</span>
                </div>
            ),
            sorter: (a: any, b: any) => a.employeeName?.localeCompare(b.employeeName),
        },
        {
            title: "Request Date",
            dataIndex: "requestDate",
            render: (text: string) => formatDate(text, config),
            sorter: (a: any, b: any) => new Date(a.requestDate).getTime() - new Date(b.requestDate).getTime(),
        },
        {
            title: "Amount Requested",
            dataIndex: "amountRequested",
            render: (text: string) => formatCurrency(parseFloat(text || '0'), config),
            sorter: (a: any, b: any) => parseFloat(a.amountRequested) - parseFloat(b.amountRequested),
        },
        {
            title: "Reason",
            dataIndex: "withdrawalReason",
            sorter: (a: any, b: any) => a.withdrawalReason?.localeCompare(b.withdrawalReason),
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (text: string) => (
                <span
                    className={`badge ${text === "active" ? "badge-success" : "badge-danger"
                        } d-inline-flex align-items-center badge-xs`}
                >
                    <i className="ti ti-point-filled me-1" />
                    {text ? text.charAt(0).toUpperCase() + text.slice(1) : ""}
                </span>
            ),
            filters: [
                { text: "Active", value: "active" },
                { text: "Inactive", value: "inactive" },
            ],
            onFilter: (value: string, record: any) => record.status === value,
        },
        {
            title: "Request Status",
            dataIndex: "requeststatus",
            filters: [
                { text: "Pending", value: "Pending" },
                { text: "Approved", value: "Approved" },
                { text: "Rejected", value: "Rejected" },
                { text: "Paid", value: "Paid" },
            ],
            onFilter: (value: string, record: any) => record.requeststatus === value,
            render: (text: string) => {
                const statusColorMap: Record<string, string> = {
                    Pending: "warning",
                    Approved: "success",
                    Rejected: "danger",
                    Paid: "info",
                };
        
                const badgeClass = statusColorMap[text] || "secondary";
        
                return (
                    <span className={`badge bg-${badgeClass} text-capitalize`}>
                        <i className="ti ti-point-filled me-1" />
                        {text}
                    </span>
                );
            },
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: any, record: any) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => {
                            showModal("edit");
                            setSelectedRowData(record);
                        }} data-bs-toggle="tooltip"
                            data-bs-placement="top"
                               title="Edit"
                    >
                        <i className="ti ti-edit" />
                    </button>
                    <button
                        onClick={() => {
                            setSelectedRowData(record);
                            setIsDeleteModalVisible(true);
                        }} data-bs-toggle="tooltip"
                            data-bs-placement="top"
                                   title="Delete"
                    >
                        <i className="ti ti-trash" />
                    </button>
                </div>
            ),
        },
    ];

   


    return (
        <>
            {/* Page Wrapper */}
            <div className="page-wrapper">
                <div className="content">
                    {/* Breadcrumb */}
                    <PageHeader
                        title="Withdrawal Request"
                        breadcrumbs={[
                            { label: <i className="ti ti-smart-home" />, path: all_routes.adminDashboard },
                            { label: "Accounting" },
                            { label: "Withdrawal Request List", isActive: true },
                        ]}
                        // listViewPath={all_routes.clientlist}
                        listViewPath="#"
                        onExportCSV={() => exportToCSV(filtereddata, 'PfWithdrawalRequestData')}
                        onExportExcel={() => exportToExcel(filtereddata, 'PfWithdrawalRequestData')}
                        onAdd={() => showModal('create')}
                        addLabel="Add Withdrawal Request"
                        exportEnabled
                        addButtonEnabled
                        rightExtra={<CollapseHeader />}
                    />
                    {/* /Breadcrumb */}
                    <div className="card">
                        <StatusFilterHeader
                            title="Withdrawal Request List"
                            selectedStatus={selectedStatus}
                            setSelectedStatus={setSelectedStatus}
                            selectedSort={selectedSort}
                            setSelectedSort={setSelectedSort}
                        />
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filtereddata} columns={columns} Selection={false} />
                        </div>
                    </div>
                </div>
                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            <PfWithdrawalRequestModal modalType={modalType} handleCancel={handleCancel} isModalVisible={isModalVisible} selectedRowData={selectedRowData} setTableKey={setTableKey} />
            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={() => selectedRowData?.pfWithdrawalId && handleDeletePfwithdrawal(selectedRowData.pfWithdrawalId)}
            />
        </>
    );
};
export default PfWithdrawalRequest;
